'use client'

import * as React from 'react'
import { Section } from '../layout/Section'
import { Container } from '../layout/Container'
import { FadeIn } from '../motion/FadeIn'

interface PrivacySection {
  heading: string
  content: string | React.ReactNode
}

interface PrivacyPageProps {
  heading?: string
  lastUpdated: string
  appName: string
  email: string
  sections?: PrivacySection[]
  companyInfo?: {
    name: string
    location: string
    registration?: string
  }
}

const defaultSections = (appName: string, email: string): PrivacySection[] => [
  {
    heading: 'Overview',
    content: `${appName} is operated by Van Moose, based in Amsterdam, Netherlands. We are committed to protecting your privacy and being transparent about the data we collect.`,
  },
  {
    heading: 'Data We Collect',
    content: (
      <>
        <p className="mb-4">
          <strong>For anonymous users:</strong> We do not collect any personal data. Data you submit is processed in real-time and not stored.
        </p>
        <p>
          <strong>For registered users:</strong> If you create an account, we collect:
        </p>
        <ul className="list-disc pl-6 mt-2 space-y-2">
          <li>Email address (for account management)</li>
          <li>Usage statistics</li>
          <li>Payment information (processed securely by Stripe)</li>
        </ul>
      </>
    ),
  },
  {
    heading: 'How We Use Your Data',
    content: (
      <ul className="list-disc pl-6 space-y-2">
        <li>To provide and maintain our service</li>
        <li>To process payments and manage subscriptions</li>
        <li>To send important service updates</li>
        <li>To improve our product based on usage patterns</li>
      </ul>
    ),
  },
  {
    heading: 'Data Sharing',
    content: (
      <>
        <p>We do not sell your data. We only share data with:</p>
        <ul className="list-disc pl-6 mt-2 space-y-2">
          <li><strong>Stripe</strong> - for payment processing</li>
          <li><strong>Clerk</strong> - for authentication services</li>
          <li><strong>Vercel</strong> - for hosting infrastructure</li>
        </ul>
      </>
    ),
  },
  {
    heading: 'Your Rights',
    content: (
      <p>
        Under GDPR, you have the right to access, correct, or delete your personal data. Contact us at{' '}
        <a href={`mailto:${email}`} className="text-[var(--accent)] hover:underline">
          {email}
        </a>{' '}
        to exercise these rights.
      </p>
    ),
  },
  {
    heading: 'Cookies',
    content: 'We use essential cookies only for authentication and session management. We do not use tracking cookies or third-party analytics that identify you personally.',
  },
]

export function PrivacyPage({
  heading = 'Privacy Policy',
  lastUpdated,
  appName,
  email,
  sections,
  companyInfo = {
    name: 'Van Moose',
    location: 'Amsterdam, Netherlands',
  },
}: PrivacyPageProps) {
  const contentSections = sections || defaultSections(appName, email)

  return (
    <Section className="min-h-[60vh]">
      <Container size="md">
        <FadeIn>
          <h1 className="headline-lg mb-2">{heading}</h1>
        </FadeIn>
        <FadeIn delay={0.1}>
          <p className="text-[var(--landing-text-tertiary)] mb-12">
            Last updated: {lastUpdated}
          </p>
        </FadeIn>

        <div className="prose prose-lg text-[var(--landing-text-secondary)] space-y-8">
          {contentSections.map((section, index) => (
            <FadeIn key={section.heading} delay={0.15 + index * 0.05}>
              <section>
                <h2 className="text-2xl font-semibold text-[var(--landing-text-primary)] mb-4">
                  {section.heading}
                </h2>
                {typeof section.content === 'string' ? (
                  <p>{section.content}</p>
                ) : (
                  section.content
                )}
              </section>
            </FadeIn>
          ))}

          <FadeIn delay={0.5}>
            <section>
              <h2 className="text-2xl font-semibold text-[var(--landing-text-primary)] mb-4">
                Contact
              </h2>
              <p>
                For privacy-related questions, contact us at{' '}
                <a href={`mailto:${email}`} className="text-[var(--accent)] hover:underline">
                  {email}
                </a>
              </p>
              <p className="mt-4 text-sm text-[var(--landing-text-tertiary)]">
                {companyInfo.name}<br />
                {companyInfo.location}
                {companyInfo.registration && <><br />{companyInfo.registration}</>}
              </p>
            </section>
          </FadeIn>
        </div>
      </Container>
    </Section>
  )
}
